import { useCallback, useRef, useState, useEffect } from 'react'
import { getPhaserGameSync } from '../PhaserGame'
import type Bootstrap from '../scenes/Bootstrap'
import store from '../stores'
import { LIVEKIT_CONNECTION_EVENT } from '../components/LiveKitDebugDisplay'

/**
 * Enhanced Room Refresh Manager
 * - Prevents room duplication during refresh operations
 * - Implements intelligent refresh strategies
 * - Provides debouncing and throttling mechanisms
 */
export function useRoomRefreshManager() {
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [lastRefreshTime, setLastRefreshTime] = useState(0)
  const [refreshCount, setRefreshCount] = useState(0)
  const [isLiveKitConnected, setIsLiveKitConnected] = useState(false)
  
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  
  // Listen for LiveKit connection status
  useEffect(() => {
    const handleLiveKitConnection = (event: CustomEvent) => {
      const status = event.detail as any
      setIsLiveKitConnected(status?.state === 'connected' || status?.state === 2) // ConnectionState.Connected = 2
    }
    
    window.addEventListener(LIVEKIT_CONNECTION_EVENT, handleLiveKitConnection as EventListener)
    
    return () => {
      window.removeEventListener(LIVEKIT_CONNECTION_EVENT, handleLiveKitConnection as EventListener)
    }
  }, [])
  
  // Configuration constants
  const REFRESH_DEBOUNCE_MS = 2000 // 2 seconds debounce
  const MIN_REFRESH_INTERVAL_MS = 5000 // 5 seconds minimum between refreshes
  const MAX_REFRESHES_PER_MINUTE = 6 // Maximum 6 refreshes per minute
  const REFRESH_WINDOW_MS = 60000 // 1 minute window for rate limiting

  /**
   * Check if refresh is allowed based on rate limiting and room state
   */
  const canRefresh = useCallback((): boolean => {
    // CRITICAL: Check if user is in a room - never refresh when in a room
    const roomState = store.getState().room
    if (roomState.roomJoined) {
      console.log('[RoomRefreshManager] Refresh blocked: user is in a room')
      return false
    }
    
    // CRITICAL: Check if LiveKit is connected - never refresh when LiveKit is active
    if (isLiveKitConnected) {
      console.log('[RoomRefreshManager] Refresh blocked: LiveKit is connected')
      return false
    }
    
    const now = Date.now()
    const timeSinceLastRefresh = now - lastRefreshTime
    
    // Check minimum interval
    if (timeSinceLastRefresh < MIN_REFRESH_INTERVAL_MS) {
      console.log(`[RoomRefreshManager] Refresh blocked: too soon (${timeSinceLastRefresh}ms < ${MIN_REFRESH_INTERVAL_MS}ms)`)
      return false
    }
    
    // Check if already refreshing
    if (isRefreshing) {
      console.log('[RoomRefreshManager] Refresh blocked: already refreshing')
      return false
    }
    
    // Check rate limiting (max 6 refreshes per minute)
    const recentRefreshes = refreshCount
    if (recentRefreshes >= MAX_REFRESHES_PER_MINUTE) {
      console.log(`[RoomRefreshManager] Refresh blocked: rate limit exceeded (${recentRefreshes}/${MAX_REFRESHES_PER_MINUTE})`)
      return false
    }
    
    return true
  }, [isRefreshing, lastRefreshTime, refreshCount, isLiveKitConnected])

  /**
   * Reset refresh count periodically
   */
  const resetRefreshCount = useCallback(() => {
    const now = Date.now()
    if (now - lastRefreshTime > REFRESH_WINDOW_MS) {
      setRefreshCount(0)
    }
  }, [lastRefreshTime])

  /**
   * Enhanced room refresh with duplication prevention
   */
  const refreshRooms = useCallback(async (force: boolean = false): Promise<boolean> => {
    try {
      // CRITICAL: Even with force, don't refresh if in a room or LiveKit is connected
      const roomState = store.getState().room
      if (roomState.roomJoined || isLiveKitConnected) {
        console.log('[RoomRefreshManager] Refresh blocked: user is in a room or LiveKit is connected (even with force flag)')
        return false
      }
      
      // Check if refresh is allowed
      if (!force && !canRefresh()) {
        return false
      }
      
      // Reset refresh count if needed
      resetRefreshCount()
      
      // Cancel any ongoing refresh
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      
      // Create new abort controller
      abortControllerRef.current = new AbortController()
      
      setIsRefreshing(true)
      setLastRefreshTime(Date.now())
      setRefreshCount(prev => prev + 1)
      
      console.log('[RoomRefreshManager] Starting room refresh...')
      
      const phaserGame = getPhaserGameSync()
      if (!phaserGame) {
        throw new Error('Phaser not yet initialized')
      }
      
      const bootstrap = phaserGame.scene?.keys?.bootstrap as Bootstrap | undefined
      if (!bootstrap || !bootstrap.network) {
        throw new Error('Network not available')
      }
      
      // Check if we're already in a lobby to avoid duplication
      // Use optional chaining and type assertion for compatibility
      const isInLobby = (bootstrap.network as any).isInLobby?.() || false
      
      if (isInLobby) {
        // Just request room state update without rejoining
        console.log('[RoomRefreshManager] Already in lobby, requesting state update')
        bootstrap.network.requestRoomState(true)
      } else {
        // Join lobby to get fresh room data
        console.log('[RoomRefreshManager] Joining lobby for fresh room data')
        await bootstrap.network.joinLobbyRoom()
      }
      
      console.log('[RoomRefreshManager] Room refresh completed successfully')
      return true
      
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        console.log('[RoomRefreshManager] Refresh aborted')
        return false
      }
      
      console.error('[RoomRefreshManager] Refresh failed:', error)
      return false
      
    } finally {
      setIsRefreshing(false)
    }
  }, [canRefresh, resetRefreshCount, isLiveKitConnected])

  /**
   * Debounced refresh function
   */
  const debouncedRefresh = useCallback((force: boolean = false) => {
    // Clear existing timeout
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current)
    }
    
    // Set new timeout
    refreshTimeoutRef.current = setTimeout(() => {
      refreshRooms(force)
    }, REFRESH_DEBOUNCE_MS)
  }, [refreshRooms])

  /**
   * Force refresh (bypasses rate limiting)
   */
  const forceRefresh = useCallback(() => {
    return refreshRooms(true)
  }, [refreshRooms])

  /**
   * Get refresh status information
   */
  const getRefreshStatus = useCallback(() => {
    const now = Date.now()
    const timeSinceLastRefresh = now - lastRefreshTime
    const timeUntilNextAllowed = Math.max(0, MIN_REFRESH_INTERVAL_MS - timeSinceLastRefresh)
    
    return {
      isRefreshing,
      canRefresh: canRefresh(),
      timeSinceLastRefresh,
      timeUntilNextAllowed,
      refreshCount,
      maxRefreshesPerMinute: MAX_REFRESHES_PER_MINUTE,
      remainingRefreshes: Math.max(0, MAX_REFRESHES_PER_MINUTE - refreshCount)
    }
  }, [isRefreshing, canRefresh, lastRefreshTime, refreshCount])

  /**
   * Cleanup function
   */
  const cleanup = useCallback(() => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current)
      refreshTimeoutRef.current = null
    }
    
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
  }, [])

  return {
    // State
    isRefreshing,
    lastRefreshTime,
    refreshCount,
    
    // Actions
    refreshRooms,
    debouncedRefresh,
    forceRefresh,
    
    // Utilities
    canRefresh,
    getRefreshStatus,
    cleanup
  }
}
